import type { Attributes, Histogram, Meter } from "@opentelemetry/api";
import { context, Span, SpanStatusCode, trace } from "@opentelemetry/api";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import type { PushMetricExporter } from "@opentelemetry/sdk-metrics";
import { MeterProvider, PeriodicExportingMetricReader } from "@opentelemetry/sdk-metrics";
import type { SpanProcessor } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION, ATTR_USER_AGENT_ORIGINAL } from "@opentelemetry/semantic-conventions";
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME, ATTR_ENDUSER_ID } from "@opentelemetry/semantic-conventions/incubating";

import { ErrorKind } from "features/render/sceneError/types";
import { Platform } from "slices/explorer/types";
import { isAppBuild } from "utils/buildType";
import { log } from "utils/log";
import { type PerfResult, setMeasurementSink } from "utils/performanceMeasure";

import type { SpanNameMap } from "./config";
import { resolveTelemetryConfig, SPAN_NAMES } from "./config";

/** Structured failure details passed to {@link TelemetryManager.endViewerLoad} and {@link TelemetryManager.endAuth}. */
export interface TelemetryFailureDetails {
    /** One of the {@link ErrorKind} enum values; recorded as the "error.kind" span attribute. */
    errorKind: ErrorKind;
    /** HTTP status code when available; recorded as the "error.status_code" span attribute. */
    statusCode?: number;
    /**
     * Human-readable description used as the OpenTelemetry span status message.
     * Defaults to `errorKind` when omitted.
     */
    message?: string;
}

const l = log.telemetry;

/** The action the user took when leaving the error screen. */
export type ErrorRecoveryAction = "refresh" | "switch_user" | "close_tab";

/** Who triggered a viewpoint load: the SDK internals or an end-user action. */
export type ViewpointSource = "system" | "user";

/** High-level error category for {@link TelemetryManager.trackCaughtException}. */
export type ErrorCategory =
    | "init" // scene load, canvas creation, GPU detection, config loading
    | "auth" // core app authentication
    | "render" // engine interactions (canvas events, picks, measurements...)
    | "data" // domain data (search, properties, viewpoints... )
    | "integration"; // third-party services (Jira, BIMCollab...)

/** How often (ms) aggregated render-perf metrics are flushed to Mixpanel + OTEL */
export const RENDER_PERF_REPORT_INTERVAL_MS = 60_000;

/** Raw render-performance sample recorded every ~200 ms by PerformanceStats */
export interface RenderPerfSample {
    fps: number;
    cpuTime: number;
    gpuTime: number;
    primitives: number;
}

/**
 * Test-only configuration for {@link TelemetryManager}.
 *
 * When provided, the constructor skips the `isAppBuild` / region guards and
 * wires the real OTel SDK to the supplied in-memory exporters.  This lets
 * tests exercise the full span/metric pipeline without HTTP calls or module
 * mocking.
 *
 * @example
 * ```ts
 * import { InMemorySpanExporter, SimpleSpanProcessor } from "@opentelemetry/sdk-trace-web";
 * import { AggregationTemporality, InMemoryMetricExporter } from "@opentelemetry/sdk-metrics";
 *
 * const spanExporter = new InMemorySpanExporter();
 * const tm = new TelemetryManager({
 *     spanProcessor: new SimpleSpanProcessor(spanExporter),
 *     metricExporter: new InMemoryMetricExporter(AggregationTemporality.CUMULATIVE),
 * });
 * ```
 */
export interface TelemetryTestConfig {
    spanProcessor: SpanProcessor;
    metricExporter: PushMetricExporter;
    metricExportIntervalMs?: number;
}

/**
 * Coerce arbitrary measurement metadata into OTel-safe span attributes.
 *
 * Primitive values (string/number/boolean) pass through unchanged; everything
 * else is JSON-serialized so an unsupported attribute type can never throw.
 * `null`/`undefined` entries are dropped.
 */
function toAttributes(meta: Record<string, unknown>): Attributes {
    const attrs: Attributes = {};

    for (const [key, value] of Object.entries(meta)) {
        if (value === undefined || value === null) {
            continue;
        }

        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            attrs[key] = value;
        } else {
            try {
                const serialized = JSON.stringify(value);
                attrs[key] = serialized === undefined ? String(value) : serialized;
            } catch {
                attrs[key] = String(value);
            }
        }
    }

    return attrs;
}

export class TelemetryManager {
    private tracer?: ReturnType<typeof trace.getTracer>;
    private meter?: Meter;
    private tracerProvider?: WebTracerProvider;
    private meterProvider?: MeterProvider;
    private isEnabled: boolean = false;
    private platform?: Platform;

    /** Attributes stamped on every span (mirrors Mixpanel `register` super-properties). */
    private globalAttributes: Record<string, string | number | boolean> = {};
    /** Identified user id (mirrors Mixpanel `identify`). */
    private userId?: string;

    /** Monotonically increasing counter stamped on widget interaction events for sequence reconstruction. */
    private widgetEventSequence: number = 0;
    /** Timestamp (ms) when the TelemetryManager was created; used to compute session-relative elapsed time. */
    private readonly sessionStartMs: number = Date.now();

    /** OTEL histograms for render-performance metrics */
    private fpsHistogram?: Histogram;
    private cpuTimeHistogram?: Histogram;
    private gpuTimeHistogram?: Histogram;
    private primitivesHistogram?: Histogram;

    /** Resolved span names — standalone vs SDK depending on build type. */
    private spanNames: SpanNameMap = SPAN_NAMES;

    private uninstallGlobalHandlers: () => void = () => {};

    constructor(testConfig?: TelemetryTestConfig) {
        try {
            if (testConfig) {
                // ── Test path: skip guards, use injected exporters ────────────
                const resource = resourceFromAttributes({
                    [ATTR_SERVICE_NAME]: "test-service",
                    [ATTR_SERVICE_VERSION]: "test",
                });

                const provider = new WebTracerProvider({
                    resource,
                    spanProcessors: [testConfig.spanProcessor],
                });

                this.tracerProvider = provider;
                this.tracer = provider.getTracer("test-lib");

                const meterProvider = new MeterProvider({
                    resource,
                    readers: [
                        new PeriodicExportingMetricReader({
                            exporter: testConfig.metricExporter,
                            exportIntervalMillis: testConfig.metricExportIntervalMs ?? RENDER_PERF_REPORT_INTERVAL_MS,
                        }),
                    ],
                });

                this.meterProvider = meterProvider;
                this.meter = meterProvider.getMeter("test-lib");
                this._registerRenderPerfHistograms();

                this.isEnabled = true;
                return;
            }

            // ── Production path ───────────────────────────────────────────────
            const config = resolveTelemetryConfig();

            if (!config) {
                l.debug("Disabled on localhost");
                return;
            }

            const { endpoint, metricsEndpoint, serviceName, libraryName, region, version, spanNames } = config;
            this.spanNames = spanNames;

            const resource = resourceFromAttributes({
                [ATTR_SERVICE_NAME]: serviceName,
                [ATTR_SERVICE_VERSION]: version,
                [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: region,
                [ATTR_USER_AGENT_ORIGINAL]: navigator.userAgent,
            });

            const exporter = new OTLPTraceExporter({
                url: endpoint,
                headers: { "Content-Type": "application/json" },
            });

            const provider = new WebTracerProvider({
                resource,
                spanProcessors: [new BatchSpanProcessor(exporter)],
            });

            // IMPORTANT: We use a local provider (without global registration) to avoid conflicts
            // with host applications (e.g., ui-service-shell) that may already have OpenTelemetry
            // initialized. This ensures our resource attributes (service.name="bim-webviewer-sdk", version,
            // region) are preserved and our spans export correctly to our regional collectors.
            //
            // Note: We intentionally do not register a context manager, so OpenTelemetry will not
            // automatically propagate parent/child relationships across async boundaries. All spans
            // we create are either root spans or explicit children created by passing a parent
            // Context (for example, in startPhase()). If you need implicit parent-child span
            // relationships across async boundaries in the future, you would need to add:
            // provider.register({ contextManager: new StackContextManager() });
            this.tracerProvider = provider;
            this.tracer = provider.getTracer(libraryName);
            // Tracing initialized successfully; enable telemetry overall.
            this.isEnabled = true;

            // ── Metrics provider ─────────────────────────────────────────────
            try {
                const metricsExporter = new OTLPMetricExporter({
                    url: metricsEndpoint,
                    headers: { "Content-Type": "application/json" },
                });

                const meterProvider = new MeterProvider({
                    resource,
                    readers: [
                        new PeriodicExportingMetricReader({
                            exporter: metricsExporter,
                            exportIntervalMillis: RENDER_PERF_REPORT_INTERVAL_MS,
                        }),
                    ],
                });

                this.meterProvider = meterProvider;
                this.meter = meterProvider.getMeter(libraryName);
                this._registerRenderPerfHistograms();
            } catch (metricsError) {
                // Metrics failed to initialize; keep tracing enabled.
                l.warn("Failed to initialize metrics; tracing remains enabled:", metricsError);
            }

            l.debug(`Initialized v${version} for region: ${region}`);
        } catch (error) {
            // Graceful degradation: telemetry fails silently, app continues
            l.warn("Failed to initialize, telemetry disabled:", error);
            this.isEnabled = false;
        }
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private _registerRenderPerfHistograms(): void {
        if (!this.meter) {
            return;
        }

        this.fpsHistogram = this.meter.createHistogram("render.fps", {
            description: "Frames per second",
            unit: "fps",
        });
        this.cpuTimeHistogram = this.meter.createHistogram("render.cpu_time_ms", {
            description: "CPU render draw time",
            unit: "ms",
        });
        this.gpuTimeHistogram = this.meter.createHistogram("render.gpu_time_ms", {
            description: "GPU render draw time",
            unit: "ms",
        });
        this.primitivesHistogram = this.meter.createHistogram("render.primitives", {
            description: "Primitive count",
            unit: "1",
        });
    }

    /**
     * Install global error handlers for unhandled exceptions and promise rejections.
     * Only called for standalone (app) builds to avoid conflicting with host app handlers.
     */
    installStandaloneGlobalHandlers(): void {
        if (!this.isEnabled) {
            return;
        }

        const onError = (event: ErrorEvent) => {
            this.trackEvent(
                "unhandled_error",
                {
                    "error.source": "onerror",
                    "error.message": event.message || "Unknown error",
                    ...(event.filename ? { "error.filename": event.filename } : {}),
                    ...(event.lineno ? { "error.lineno": event.lineno } : {}),
                    ...(event.colno ? { "error.colno": event.colno } : {}),
                },
                event.error instanceof Error ? event.error : undefined,
            );
        };

        const onRejection = (event: PromiseRejectionEvent) => {
            const error = event.reason instanceof Error ? event.reason : undefined;
            const message = error?.message ?? (typeof event.reason === "string" ? event.reason : "Unknown rejection");

            this.trackEvent(
                "unhandled_error",
                { "error.source": "unhandledrejection", "error.message": message },
                error,
            );
        };

        window.addEventListener("error", onError);
        window.addEventListener("unhandledrejection", onRejection);

        this.uninstallGlobalHandlers = () => {
            window.removeEventListener("error", onError);
            window.removeEventListener("unhandledrejection", onRejection);
        };

        l.debug("Global error handlers installed");
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Record a single render-performance sample (called every ~200 ms by PerformanceStats).
     *
     * Each value is recorded on its corresponding OTEL histogram. The SDK's
     * {@link PeriodicExportingMetricReader} aggregates and exports the distribution
     * (min, max, sum, count, bucket counts) every {@link RENDER_PERF_REPORT_INTERVAL_MS}.
     *
     * The method is a no-op when telemetry is disabled (LOCAL region).
     *
     * @param sample - fps, cpuTime, gpuTime, primitives measured at this tick
     */
    recordRenderPerf(sample: RenderPerfSample): void {
        if (!this.isEnabled) {
            return;
        }

        this.fpsHistogram?.record(sample.fps);
        this.cpuTimeHistogram?.record(sample.cpuTime);
        this.gpuTimeHistogram?.record(sample.gpuTime);
        this.primitivesHistogram?.record(sample.primitives);
    }

    /**
     * Begin a span for a `startMeasure()` measurement, named `perf.<name>`.
     *
     * When `parentSpan` is provided the new span is linked to it via explicit
     * context propagation (the same mechanism as {@link startPhase}), so nested
     * measurements form a parent/child trace. When it is null the span is a
     * root span. The returned span is stored as the measurement's opaque handle
     * and later passed to {@link endMeasurement}.
     *
     * Wired up via {@link setMeasurementSink}; not called directly by app code.
     *
     * @param name       - The measurement name (becomes `perf.<name>`).
     * @param parentSpan - Enclosing measurement's span, or null for a root span.
     * @returns The started span, or null when telemetry is disabled.
     */
    startMeasurement(name: string, parentSpan: Span | null): Span | null {
        if (!this.isEnabled || !this.tracer) {
            return null;
        }

        let span: Span;
        if (parentSpan) {
            const parentContext = trace.setSpan(context.active(), parentSpan);
            span = this.tracer.startSpan(`perf.${name}`, {}, parentContext);
        } else {
            span = this.tracer.startSpan(`perf.${name}`, { root: true });
        }

        this._stampSpan(span);
        span.setAttribute("perf.name", name);

        return span;
    }

    /**
     * Finalize a measurement span started by {@link startMeasurement}.
     *
     * Stamps the total duration and top-level `meta` as attributes, replays each
     * `mark` as a span event positioned at its relative offset (carrying
     * `perf.elapsed_ms` plus any mark metadata), then ends the span.
     *
     * Because the span was started eagerly in {@link startMeasurement}, its
     * start/end timestamps reflect the real elapsed time without reconstruction.
     *
     * @param span   - The span returned from startMeasurement() (no-op when null).
     * @param result - The measurement returned by `PerfMeasurement.end()`.
     */
    endMeasurement(span: Span | null, result: PerfResult): void {
        if (!span) {
            return;
        }

        span.setAttribute("perf.total_ms", result.totalMs);

        if (result.meta) {
            for (const [key, value] of Object.entries(toAttributes(result.meta))) {
                if (value !== undefined) {
                    span.setAttribute(key, value);
                }
            }
        }

        // The span started ~totalMs ago; reconstruct that base so mark events
        // land at the correct offset within the span's timeline.
        const markBase = Date.now() - result.totalMs;
        for (const mark of result.marks) {
            span.addEvent(
                mark.label,
                { "perf.elapsed_ms": mark.elapsedMs, ...(mark.meta ? toAttributes(mark.meta) : {}) },
                markBase + mark.elapsedMs,
            );
        }

        const isError = result.meta?.error === true;
        span.setStatus({ code: isError ? SpanStatusCode.ERROR : SpanStatusCode.OK });
        span.end();

        l.debug(`Measurement recorded: ${result.name} (${result.totalMs.toFixed(1)}ms)`);
    }

    /**
     * Set the platform that will be stamped on every subsequent span.
     * Must be called before any spans are started (typically during Webviewer init).
     *
     * @param platform - The platform value from InitOptions (e.g. Platform.Web, Platform.iOS, Platform.Android).
     */
    setPlatform(platform: Platform): void {
        this.platform = platform;
        l.debug(`Platform set to: ${platform}`);
    }

    /**
     * Flush any pending telemetry and stop all background timers/exporters.
     * NOTE: shutdown() is a test-cleanup affordance, not a production API.
     *
     * Call this when the TelemetryManager is no longer needed (e.g. in test
     * teardown) to prevent leaked timers from the {@link PeriodicExportingMetricReader}
     * and the {@link BatchSpanProcessor}.
     *
     * Safe to call multiple times or when telemetry is disabled (no-op).
     */
    async shutdown(): Promise<void> {
        if (!this.isEnabled) {
            return;
        }

        this.isEnabled = false;
        this.uninstallGlobalHandlers();

        const results = await Promise.allSettled([this.tracerProvider?.shutdown(), this.meterProvider?.shutdown()]);

        for (const result of results) {
            if (result.status === "rejected") {
                l.warn("Provider shutdown error:", result.reason);
            }
        }

        l.debug("Shutdown complete");
    }

    /**
     * Stamp common attributes (platform, user.id, globalAttributes) on a span.
     * Called by both {@link createSpan} and {@link startPhase} so every span
     * gets a consistent set of base attributes.
     */
    private _stampSpan(span: Span): void {
        if (this.platform) {
            span.setAttribute("platform", this.platform);
        }

        if (this.userId) {
            // custom attribute consumed by Honeycomb
            span.setAttribute("user.id", this.userId);
            // OTEL semantic convention mapped to top-level UserId in Azure Monitor (standalone)
            span.setAttribute(ATTR_ENDUSER_ID, this.userId);
        }

        for (const [key, value] of Object.entries(this.globalAttributes)) {
            span.setAttribute(key, value);
        }
    }

    /**
     * Create a span with common attributes (e.g. platform) stamped automatically.
     * All public start*() methods should use this instead of this.tracer.startSpan() directly
     * so that base attributes are applied consistently to every span.
     */
    private createSpan(name: string): Span | null {
        if (!this.isEnabled || !this.tracer) {
            return null;
        }

        const span = this.tracer.startSpan(name);
        this._stampSpan(span);

        return span;
    }

    /**
     * Start viewer load span - tracks initialization performance and success
     *
     * @param sceneId - Optional scene/model ID being loaded
     * @returns The span to be ended when initialization completes, or null if telemetry is disabled
     */
    startViewerLoad(sceneId?: string): Span | null {
        const span = this.createSpan(this.spanNames.VIEWER_LOAD);
        if (!span) {
            return null;
        }

        if (sceneId) {
            span.setAttribute("scene.id", sceneId);
        }

        l.debug("Viewer load started");
        return span;
    }

    /**
     * Start a child span representing a named initialization phase.
     *
     * The child span is linked to the parent via explicit context propagation
     * (no global context manager required). This gives native waterfall
     * visualisation, per-phase error attribution, and queryability in tracing
     * back-ends (Jaeger, Honeycomb, etc.).
     *
     * @param parentSpan - The parent span (e.g. from startViewerLoad). No-op when null.
     * @param name       - Phase name; the child span will be named `phase.<name>`.
     * @returns The child span, or null if telemetry is disabled / parentSpan is null.
     */
    startPhase(parentSpan: Span | null, name: string): Span | null {
        if (!parentSpan || !this.isEnabled || !this.tracer) {
            return null;
        }

        const parentContext = trace.setSpan(context.active(), parentSpan);
        const childSpan = this.tracer.startSpan(`phase.${name}`, {}, parentContext);
        this._stampSpan(childSpan);

        l.debug(`Phase "${name}" started`);
        return childSpan;
    }

    /**
     * End a child phase span previously started with {@link startPhase}.
     *
     * @param span    - The phase span to close (no-op when null).
     * @param success - Whether the phase completed successfully (default true).
     * @param error   - Optional Error to record when success is false.
     */
    endPhase(span: Span | null, success: boolean = true, error?: Error): void {
        if (!span) {
            return;
        }

        if (success) {
            span.setStatus({ code: SpanStatusCode.OK });
        } else {
            span.setStatus({
                code: SpanStatusCode.ERROR,
                message: error?.message,
            });
            if (error) {
                span.recordException(error);
            }
        }

        span.end();
        l.debug(`Phase ended: ${success ? "OK" : "ERROR"}`);
    }

    /**
     * Null-safe wrapper around {@link Span.setAttribute}.
     *
     * Use this when you hold a raw `Span | null` reference and want to stamp a
     * single attribute without an inline null-check at every call site.
     *
     * @param span  - The span to stamp (no-op when null).
     * @param key   - Attribute key.
     * @param value - Attribute value.
     */
    setSpanAttribute(span: Span | null, key: string, value: string | number | boolean): void {
        span?.setAttribute(key, value);
    }

    /**
     * End viewer load span with success/failure status.
     * This determines "good vs bad" for SLOs.
     *
     * @param span    - The span returned from startViewerLoad() (or null if telemetry disabled).
     * @param success - Whether initialization succeeded.
     * @param failure - Structured failure details (only meaningful when success is false).
     */
    endViewerLoad(span: Span | null, success: boolean, failure?: TelemetryFailureDetails): void {
        if (!span) {
            return; // Telemetry disabled, nothing to do
        }

        // CRITICAL: This attribute is used for Availability SLO
        // Query: WHERE name = "bim-webviewer-sdk.viewer.load" AND success = true
        span.setAttribute("success", success);

        if (failure?.errorKind !== undefined) {
            span.setAttribute("error.kind", failure.errorKind);
        }

        if (failure?.statusCode !== undefined) {
            span.setAttribute("error.status_code", failure.statusCode);
        }

        // Set OpenTelemetry status (used for error tracking)
        span.setStatus({
            code: success ? SpanStatusCode.OK : SpanStatusCode.ERROR,
            message: failure?.message ?? failure?.errorKind,
        });

        // span.end() automatically captures duration_ms for Performance SLO
        // Query: P95(duration_ms) WHERE name = "bim-webviewer-sdk.viewer.load" AND success = true
        span.end();

        l.debug(`Viewer load ended: ${success ? "SUCCESS" : "FAILED"}`);
    }

    /**
     * Record how the user responded to the error screen.
     *
     * Creates a single, immediately-ended span so that the action is visible
     * in the OTEL backend correlated with the scene load failure.
     *
     * @param errorKind - The ErrorKind that was displayed on the error screen.
     * @param action    - What the user did: clicked "Refresh"/"Switch User", or closed the tab.
     */
    recordErrorRecovery(errorKind: ErrorKind, action: ErrorRecoveryAction): void {
        const span = this.createSpan(this.spanNames.ERROR_RECOVERY);
        if (!span) {
            return;
        }

        span.setAttribute("error.kind", errorKind);
        span.setAttribute("recovery.action", action);
        span.setStatus({ code: SpanStatusCode.OK });
        span.end();

        l.debug(`Error recovery recorded: action=${action}, errorKind=${errorKind}`);
    }

    /**
     * Start an auth span – tracks the token-exchange / user-fetch step independently
     * from the viewer-load span that covers the full initialisation in useHandleInit.
     *
     * Keeping auth as its own span prevents double-counting sessions in the
     * Availability SLO (which only queries VIEWER_LOAD spans).
     *
     * @returns The span to be ended when auth completes, or null if telemetry is disabled.
     */
    startAuth(): Span | null {
        const span = this.createSpan(this.spanNames.AUTH);
        if (!span) {
            return null;
        }
        l.debug("Auth started");
        return span;
    }

    /**
     * End the auth span opened by startAuth().
     *
     * @param span    - The span returned from startAuth() (or null if telemetry disabled).
     * @param success - Whether authentication succeeded.
     * @param failure - Structured failure details (only meaningful when success is false).
     */
    endAuth(span: Span | null, success: boolean, failure?: TelemetryFailureDetails): void {
        if (!span) {
            return;
        }

        span.setAttribute("success", success);

        if (failure?.errorKind !== undefined) {
            span.setAttribute("error.kind", failure.errorKind);
        }

        if (failure?.statusCode !== undefined) {
            span.setAttribute("error.status_code", failure.statusCode);
        }

        span.setStatus({
            code: success ? SpanStatusCode.OK : SpanStatusCode.ERROR,
            message: failure?.message ?? failure?.errorKind,
        });

        span.end();

        l.debug(`Auth ended: ${success ? "SUCCESS" : "FAILED"}`);
    }

    // ── Product-analytics helpers (parallel Mixpanel) ─────────────────────

    /**
     * Identify the current user (mirrors Mixpanel `identify` + `people.set`).
     *
     * The userId is stamped as `user.id` and `enduser.id` on every span created
     * after this call. Additional properties (e.g. organization) are stored as
     * global attributes.
     *
     * @param userId     - Unique user identifier.
     * @param properties - Optional key/value pairs about the user (e.g. `{ "user.org": "Acme" }`).
     */
    identifyUser(userId: string, properties?: Record<string, string | number | boolean>): void {
        this.userId = userId;

        if (properties) {
            for (const [key, value] of Object.entries(properties)) {
                this.globalAttributes[key] = value;
            }
        }

        l.debug(`User identified: ${userId}`);
    }

    /**
     * Register global attributes stamped on every subsequent span
     * (mirrors Mixpanel `register` super-properties).
     *
     * @param properties - Key/value pairs to attach to all future spans.
     */
    setGlobalAttributes(properties: Record<string, string | number | boolean>): void {
        for (const [key, value] of Object.entries(properties)) {
            this.globalAttributes[key] = value;
        }

        l.debug("Global attributes registered", Object.keys(properties));
    }

    /**
     * Track a product-analytics event as a short-lived span
     * (mirrors Mixpanel `track`).
     *
     * A span is created, event-specific properties are set as attributes,
     * and the span is immediately ended so it is exported as a single
     * point-in-time event.
     *
     * @param eventName  - Human-readable event name (e.g. "Opened Widget").
     * @param properties - Optional attributes specific to this event.
     * @param error      - Optional Error to record. When provided the span status
     *                     is set to {@link SpanStatusCode.ERROR} and the exception
     *                     is attached; otherwise the span is marked OK.
     */
    trackEvent(eventName: string, properties?: Attributes, error?: Error): void {
        const span = this.createSpan(eventName);
        if (!span) {
            return;
        }

        if (properties) {
            for (const [key, value] of Object.entries(properties)) {
                if (value !== undefined) {
                    span.setAttribute(key, value);
                }
            }
        }

        if (error) {
            span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
            span.recordException(error);
        } else {
            span.setStatus({ code: SpanStatusCode.OK });
        }
        span.end();

        l.debug(`Event tracked: ${eventName}`);
    }

    /**
     * Track a caught exception as a short-lived error span.
     *
     * Creates a span named `"caught_exception"` with standardized attributes
     * for consistent querying across all catch blocks. Internally delegates to
     * {@link trackEvent} for span lifecycle management.
     *
     * @param options.source          - Call site identifier (e.g. `"useHandleInit.gpuDetection"`)
     * @param options.category        - High-level error category for dashboard filtering
     * @param options.error           - The caught value
     * @param options.severity        - `"warning"` (default) or `"error"`
     * @param options.extraAttributes - Additional span attributes beyond the standard set
     */
    trackCaughtException(options: {
        source: string;
        category: ErrorCategory;
        error: unknown;
        severity?: "warning" | "error";
        extraAttributes?: Attributes;
    }): void {
        const { source, category, error, severity = "warning", extraAttributes } = options;
        let err: Error;

        if (error instanceof Error) {
            err = error;
        } else {
            let msg: string;

            if (typeof error === "string") {
                msg = error;
            } else {
                try {
                    const json = JSON.stringify(error);
                    msg = json === undefined ? String(error) : json;
                } catch {
                    msg = String(error);
                }
            }

            err = new Error(msg);
        }

        const properties: Attributes = {
            "error.source": source,
            "error.category": category,
            "error.severity": severity,
        };

        if (extraAttributes) {
            for (const [key, value] of Object.entries(extraAttributes)) {
                if (value !== undefined) {
                    properties[key] = value;
                }
            }
        }

        this.trackEvent("caught_exception", properties, err);
    }

    /**
     * Track a pageview event as a short-lived span
     * (mirrors Mixpanel `track_pageview`).
     *
     * @param properties - Optional attributes for the pageview (e.g. scene ID, title).
     */
    trackPageview(properties?: Record<string, string | number | boolean>): void {
        this.trackEvent("pageview", properties);
    }

    /**
     * Track a widget open or close interaction with sequencing metadata.
     *
     * Each call auto-stamps:
     * - `widget.sequence`           – monotonically increasing counter (1, 2, 3 …)
     *                                 enabling reconstruction of the exact open/close order.
     * - `widget.session_elapsed_ms` – milliseconds since session start for time-based analysis.
     * - `widget.action`             – `"open"` or `"close"`.
     * - `widget.open_widgets`       – comma-separated snapshot of widget keys that are open
     *                                 at the time of the event (after the action has been applied).
     * - `widget.open_count`         – number of widgets open at the time of the event.
     *
     * Additional event-specific properties (e.g. `close.reason`, `Duration`)
     * are merged in as extra span attributes.
     *
     * @param action          - `"open"` or `"close"`.
     * @param widgetKey       - The key of the widget being opened/closed.
     * @param openWidgets     - Snapshot of widget keys currently open (before dispatch).
     * @param extraProperties - Optional additional attributes for the span.
     */
    trackWidgetInteraction(
        action: "open" | "close",
        widgetKey: string,
        openWidgets: readonly string[],
        extraProperties?: Record<string, string | number | boolean>,
    ): void {
        this.widgetEventSequence++;

        const widgetsAfter =
            action === "open" ? [...openWidgets, widgetKey] : openWidgets.filter((w) => w !== widgetKey);

        this.trackEvent("Widget Interaction", {
            "widget.key": widgetKey,
            "widget.action": action,
            "widget.sequence": this.widgetEventSequence,
            "widget.session_elapsed_ms": Date.now() - this.sessionStartMs,
            "widget.open_widgets": widgetsAfter.join(","),
            "widget.open_count": widgetsAfter.length,
            ...extraProperties,
        });
    }

    /**
     * Start a viewpoint.loaded span – tracks the duration and outcome of applying
     * a viewpoint, whether triggered by the system (initial load) or by the user
     * (e.g. via the public `setViewpoint` API or bookmark selection).
     *
     * @param source - `"system"` for SDK-internal calls; `"user"` for end-user actions.
     * @returns The span to be ended when the viewpoint has been applied, or null if telemetry is disabled.
     */
    startViewpointLoad(source: ViewpointSource): Span | null {
        const span = this.createSpan(this.spanNames.VIEWPOINT_LOADED);
        if (!span) {
            return null;
        }
        span.setAttribute("viewpoint.source", source);
        l.debug(`Viewpoint load started (source=${source})`);
        return span;
    }

    /**
     * End a viewpoint.loaded span opened by {@link startViewpointLoad}.
     *
     * @param span    - The span returned from startViewpointLoad() (or null if telemetry disabled).
     * @param success - Whether the viewpoint was applied successfully.
     * @param errorMessage - Optional human-readable error message when success is false.
     */
    endViewpointLoad(span: Span | null, success: boolean, errorMessage?: string): void {
        if (!span) {
            return;
        }

        // CRITICAL: This attribute is used for viewpoint load success tracking.
        // Query: WHERE name = "bim-webviewer-sdk.viewpoint.loaded" AND success = true
        span.setAttribute("success", success);

        span.setStatus({
            code: success ? SpanStatusCode.OK : SpanStatusCode.ERROR,
            message: errorMessage,
        });

        // span.end() automatically captures duration_ms for Performance SLO
        span.end();

        l.debug(`Viewpoint load ended: ${success ? "SUCCESS" : "FAILED"}`);
    }
}

// Singleton instance - initialized once per page load
export const telemetry = new TelemetryManager();

// Map `startMeasure()` lifecycle onto OpenTelemetry spans, preserving the
// parent/child relationship between nested measurements. The opaque handle
// threaded through the sink is the measurement's span.
setMeasurementSink({
    onStart: (name, parent) => telemetry.startMeasurement(name, (parent as Span | null | undefined) ?? null),
    onEnd: (handle, result) => telemetry.endMeasurement((handle as Span | null | undefined) ?? null, result),
});

if (isAppBuild()) {
    telemetry.installStandaloneGlobalHandlers();
}
